// src/pages/games/LetterSoundMatchGame.js
import React, { useEffect, useState, useRef, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { RotateCcw, HelpCircle } from 'lucide-react';
import { db } from '../../firebase';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import useAudio from '../../hooks/useAudio';
import useAnalytics from '../../hooks/useAnalytics';

const LETTERS = [
  { char: 'ا', letterSound: '/sounds/letters/sound/alif.mp3', nameSound: '/sounds/letters/name/alif.mp3' },
  { char: 'ب', letterSound: '/sounds/letters/sound/ba.mp3', nameSound: '/sounds/letters/name/ba.mp3' },
  { char: 'ت', letterSound: '/sounds/letters/sound/ta.mp3', nameSound: '/sounds/letters/name/ta.mp3' },
  { char: 'ث', letterSound: '/sounds/letters/sound/tha.mp3', nameSound: '/sounds/letters/name/tha.mp3' },
  { char: 'ج', letterSound: '/sounds/letters/sound/jiim.mp3', nameSound: '/sounds/letters/name/jiim.mp3' },
  { char: 'ح', letterSound: '/sounds/letters/sound/hha.mp3', nameSound: '/sounds/letters/name/hha.mp3' },
  { char: 'خ', letterSound: '/sounds/letters/sound/kha.mp3', nameSound: '/sounds/letters/name/kha.mp3' },
  { char: 'د', letterSound: '/sounds/letters/sound/daal.mp3', nameSound: '/sounds/letters/name/daal.mp3' },
  { char: 'ذ', letterSound: '/sounds/letters/sound/thaal.mp3', nameSound: '/sounds/letters/name/thaal.mp3' },
  { char: 'ر', letterSound: '/sounds/letters/sound/ra.mp3', nameSound: '/sounds/letters/name/ra.mp3' },
  { char: 'ز', letterSound: '/sounds/letters/sound/zay.mp3', nameSound: '/sounds/letters/name/zay.mp3' },
  { char: 'س', letterSound: '/sounds/letters/sound/siin.mp3', nameSound: '/sounds/letters/name/siin.mp3' },
  { char: 'ش', letterSound: '/sounds/letters/sound/shiin.mp3', nameSound: '/sounds/letters/name/shiin.mp3' },
  { char: 'ص', letterSound: '/sounds/letters/sound/saad.mp3', nameSound: '/sounds/letters/name/saad.mp3' },
  { char: 'ض', letterSound: '/sounds/letters/sound/daad.mp3', nameSound: '/sounds/letters/name/daad.mp3' },
  { char: 'ط', letterSound: '/sounds/letters/sound/taa.mp3', nameSound: '/sounds/letters/name/taa.mp3' },
  { char: 'ظ', letterSound: '/sounds/letters/sound/thaa.mp3', nameSound: '/sounds/letters/name/thaa.mp3' },
  { char: 'ع', letterSound: '/sounds/letters/sound/ayn.mp3', nameSound: '/sounds/letters/name/ayn.mp3' },
  { char: 'غ', letterSound: '/sounds/letters/sound/ghayn.mp3', nameSound: '/sounds/letters/name/ghayn.mp3' },
  { char: 'ف', letterSound: '/sounds/letters/sound/fa.mp3', nameSound: '/sounds/letters/name/fa.mp3' },
  { char: 'ق', letterSound: '/sounds/letters/sound/qaf.mp3', nameSound: '/sounds/letters/name/qaf.mp3' },
  { char: 'ك', letterSound: '/sounds/letters/sound/kaf.mp3', nameSound: '/sounds/letters/name/kaf.mp3' },
  { char: 'ل', letterSound: '/sounds/letters/sound/lam.mp3', nameSound: '/sounds/letters/name/lam.mp3' },
  { char: 'م', letterSound: '/sounds/letters/sound/miim.mp3', nameSound: '/sounds/letters/name/miim.mp3' },
  { char: 'ن', letterSound: '/sounds/letters/sound/nuun.mp3', nameSound: '/sounds/letters/name/nuun.mp3' },
  { char: 'ه', letterSound: '/sounds/letters/sound/ha.mp3', nameSound: '/sounds/letters/name/ha.mp3' },
  { char: 'و', letterSound: '/sounds/letters/sound/waw.mp3', nameSound: '/sounds/letters/name/waw.mp3' },
  { char: 'ي', letterSound: '/sounds/letters/sound/ya.mp3', nameSound: '/sounds/letters/name/ya.mp3' },
];

const getRandomLetters = (correct, count = 6) => {
  const others = LETTERS.filter(l => l.char !== correct.char);
  const shuffled = others.sort(() => 0.5 - Math.random()).slice(0, count - 1);
  return [...shuffled, correct].sort(() => 0.5 - Math.random());
};

const LetterSoundMatchGame = () => {
  // Initialize audio hook
  const { saveSessionData } = useAnalytics();
  const {
    playCorrectSound,
    playIncorrectSound,
    startBackgroundMusic,
    AudioElements
  } = useAudio();

  // Game state
  const [targetLetter, setTargetLetter] = useState(null);
  const [choices, setChoices] = useState([]);
  const [isCorrect, setIsCorrect] = useState(null);
  const [disableButtons, setDisableButtons] = useState(false);
  const [soundMode, setSoundMode] = useState('sound');
  const [stats, setStats] = useState({ rounds: 0, correct: 0, streak: 0, bestStreak: 0 });
  const [showInstructions, setShowInstructions] = useState(false);
  const [celebrations, setCelebrations] = useState(false);
  const [sparkles, setSparkles] = useState(false);
  
  const audioRef = useRef(null);
  const audioTimeoutRef = useRef(null);
  const userId = getAuth().currentUser?.uid;

  const [sessionStartTime, setSessionStartTime] = useState(Date.now());
  const [sessionMistakes, setSessionMistakes] = useState([]);
  const [sessionCorrect, setSessionCorrect] = useState(0);
  const [sessionTotal, setSessionTotal] = useState(0);

  // Initialize game
  useEffect(() => {
    setSessionStartTime(Date.now());
    generateNewRound();

    // Start background music
    const musicTimer = setTimeout(() => {
      startBackgroundMusic();
    }, 1000);
    
    return () => clearTimeout(musicTimer);
  }, [startBackgroundMusic]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      saveGameSession();
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current.src = '';
        } catch (e) {
          // Ignore cleanup errors
        }
        audioRef.current = null;
      }
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
        audioTimeoutRef.current = null;
      }
      confetti.reset();
    };
  }, []);

  const updateFirebaseProgress = async (newStats) => {
    if (!userId) return;
    const docRef = doc(db, 'users', userId);
    try {
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        await updateDoc(docRef, {
          'progress.lettersoundmatchgame': {
            ...newStats,
            updatedAt: serverTimestamp(),
          },
        });
      } else {
        await setDoc(docRef, {
          progress: {
            lettersoundmatchgame: {
              ...newStats,
              createdAt: serverTimestamp(),
            },
          },
        });
      }
    } catch (e) {
      console.error('Failed to update progress:', e);
    }
  };

  const triggerCelebration = useCallback(() => {
    setCelebrations(true);
    setSparkles(true);
    
    confetti({
      particleCount: 50,
      spread: 60,
      origin: { y: 0.7 },
      colors: ['#00ff88', '#00ddff', '#8844ff'],
      shapes: ['circle'],
      gravity: 1.2,
      drift: 0
    });

    setTimeout(() => {
      setCelebrations(false);
      setSparkles(false);
    }, 1500);
  }, []);

  const generateNewRound = () => {
    const letter = LETTERS[Math.floor(Math.random() * LETTERS.length)];
    const options = getRandomLetters(letter);
    setTargetLetter(letter);
    setChoices(options);
    setIsCorrect(null);
    setDisableButtons(false);
    if (sessionTotal > 0 && sessionTotal % 10 === 0) {
      saveGameSession();
    }
  };

  const playSound = useCallback(async () => {
    if (!targetLetter) return;
    
    // Clear any existing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = '';
      audioRef.current = null;
    }
    
    if (audioTimeoutRef.current) {
      clearTimeout(audioTimeoutRef.current);
      audioTimeoutRef.current = null;
    }

    const file = soundMode === 'name' ? targetLetter.nameSound : targetLetter.letterSound;
    
    try {
      const audio = new Audio();
      audioRef.current = audio;
      
      // Set up a safety timeout
      audioTimeoutRef.current = setTimeout(() => {
        console.log('🔊 Audio timeout - stopping playback');
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current.src = '';
          audioRef.current = null;
        }
      }, 5000);
      
      const handleEnded = () => {
        if (audioTimeoutRef.current) {
          clearTimeout(audioTimeoutRef.current);
          audioTimeoutRef.current = null;
        }
        audioRef.current = null;
      };
      
      const handleError = (error) => {
        console.warn('🔊 Audio error:', error);
        if (audioTimeoutRef.current) {
          clearTimeout(audioTimeoutRef.current);
          audioTimeoutRef.current = null;
        }
        audioRef.current = null;
      };
      
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);
      
      audio.volume = 0.8;
      audio.src = file;
      
      await audio.play();
      
    } catch (error) {
      console.error('🔊 Failed to play audio:', error);
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
        audioTimeoutRef.current = null;
      }
    }
  }, [targetLetter, soundMode]);

  const saveGameSession = useCallback(() => {
    if (sessionTotal === 0) return; // Don't save empty sessions
    
    const sessionData = {
      gameType: 'letterSoundMatch',
      level: 1, // This game doesn't have levels
      sessionStartTime,
      sessionDuration: Math.round((Date.now() - sessionStartTime) / 1000),
      totalScore: sessionCorrect * 10, // Simple scoring
      accuracy: Math.round((sessionCorrect / sessionTotal) * 100),
      attempts: sessionTotal,
      correctAnswers: sessionCorrect,
      mistakes: sessionMistakes,
      starsEarned: sessionCorrect >= 10 ? 3 : sessionCorrect >= 5 ? 2 : sessionCorrect >= 3 ? 1 : 0,
      soundMode: soundMode,
      bestStreak: stats.bestStreak
    };
    saveSessionData(sessionData);
  }, [sessionStartTime, sessionCorrect, sessionTotal, sessionMistakes, soundMode, stats.bestStreak, saveSessionData]);

  const handleChoice = (choice) => {
    setDisableButtons(true);
    const correct = choice.char === targetLetter.char;
    const updatedStats = { ...stats, rounds: stats.rounds + 1 };

    // Add session tracking
    setSessionTotal(prev => prev + 1);

    if (correct) {
      setSessionCorrect(prev => prev + 1);
      updatedStats.correct += 1;
      updatedStats.streak += 1;
      updatedStats.bestStreak = Math.max(updatedStats.streak, stats.bestStreak);
      triggerCelebration();
      playCorrectSound();
    } else {
      // Track mistakes
      setSessionMistakes(prev => [...prev, {
        timestamp: Date.now(),
        type: 'wrong_letter_choice',
        attempted: choice.char,
        correct: targetLetter.char,
        soundMode: soundMode
      }]);

      updatedStats.streak = 0;
      playIncorrectSound();
    }

    setStats(updatedStats);
    updateFirebaseProgress({
      rounds: updatedStats.rounds,
      correct: updatedStats.correct,
      accuracy: ((updatedStats.correct / updatedStats.rounds) * 100).toFixed(1),
      streak: updatedStats.streak,
      bestStreak: updatedStats.bestStreak,
    });

    setIsCorrect(correct);
    // Save session at milestones (every 25 rounds, or when reaching certain streaks)
    const newSessionTotal = sessionTotal + 1;
    if (newSessionTotal % 25 === 0 || updatedStats.streak === 10 || updatedStats.streak === 20) {
      setTimeout(() => saveGameSession(), 1000); // Delay to ensure state is updated
    }
    setTimeout(() => generateNewRound(), correct ? 1500 : 1200);
  };

  const resetGame = () => {
    setStats({ rounds: 0, correct: 0, streak: 0, bestStreak: 0 });
    generateNewRound();
  };

  if (!targetLetter) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-400 via-blue-500 to-purple-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm w-full">
          <div className="text-center">
            <div className="text-4xl md:text-6xl mb-4 animate-spin">🎵</div>
            <div className="text-purple-600 text-xl md:text-3xl font-bold animate-pulse">Loading Letter Match...</div>
            <div className="text-gray-600 text-sm md:text-lg mt-2">Getting ready for fun!</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-400 via-blue-500 to-purple-600 p-4">
      <AudioElements />
      
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-3 md:mb-4">
          <h1 className="text-2xl md:text-4xl font-black text-white mb-1 md:mb-2 drop-shadow-2xl">
            🎵 Phonic Fun 🎵
          </h1>
          <p className="text-base md:text-xl text-white font-bold opacity-90 drop-shadow-lg">
            Listen and match the Arabic letter!
          </p>
        </div>

        {/* Stats Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-3 md:p-4 mb-3 md:mb-4 border-4 border-yellow-300">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-2 md:mb-3">
            <div className="text-center bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl p-2 md:p-3 text-white">
              <div className="text-xl md:text-2xl mb-1">🎯</div>
              <p className="font-bold text-base md:text-lg">{stats.rounds}</p>
              <p className="text-xs font-bold">Rounds</p>
            </div>
            <div className="text-center bg-gradient-to-br from-green-400 to-green-600 rounded-2xl p-2 md:p-3 text-white">
              <div className="text-xl md:text-2xl mb-1">✅</div>
              <p className="font-bold text-base md:text-lg">{stats.correct}</p>
              <p className="text-xs font-bold">Correct</p>
            </div>
            <div className="text-center bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl p-2 md:p-3 text-white">
              <div className="text-xl md:text-2xl mb-1">🔥</div>
              <p className="font-bold text-base md:text-lg">{stats.streak}</p>
              <p className="text-xs font-bold">Streak</p>
            </div>
            <div className="text-center bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl p-2 md:p-3 text-white">
              <div className="text-xl md:text-2xl mb-1">🏆</div>
              <p className="font-bold text-base md:text-lg">{stats.bestStreak}</p>
              <p className="text-xs font-bold">Best</p>
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-xl md:text-2xl font-bold text-indigo-600">
              Accuracy: {stats.rounds === 0 ? '0%' : ((stats.correct / stats.rounds) * 100).toFixed(1) + '%'}
            </p>
          </div>
        </div>

        {/* Game Board */}
        <div className="bg-white rounded-3xl shadow-2xl p-3 md:p-4 mb-3 border-4 border-pink-300">
          {/* Sound Mode Toggle */}
          <div className="flex justify-center items-center gap-3 mb-3">
            <span className="text-base md:text-lg font-bold text-gray-700">Sound Type:</span>
            <button
              onClick={() => setSoundMode(prev => (prev === 'sound' ? 'name' : 'sound'))}
              className={`px-3 md:px-4 py-2 rounded-2xl font-bold text-base md:text-lg transition-all duration-200 border-3 ${
                soundMode === 'sound' 
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white border-blue-300 shadow-lg' 
                  : 'bg-gradient-to-r from-purple-500 to-purple-600 text-white border-purple-300 shadow-lg'
              }`}
            >
              {soundMode === 'sound' ? '🎵 Letter Sound' : '🔤 Letter Name'}
            </button>
          </div>

          {/* Play Sound Button */}
          <div className="text-center mb-4">
            <button
              onClick={playSound}
              className="px-8 md:px-12 py-4 md:py-5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-3xl font-black text-xl md:text-2xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300 border-3 border-white"
            >
              🔊 Play {soundMode === 'sound' ? 'Letter Sound' : 'Letter Name'}
            </button>
          </div>

          {/* Letter Choices */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-4">
            {choices.map((choice, idx) => {
              let buttonClass = "w-full aspect-square rounded-3xl font-black text-5xl md:text-6xl transition-all duration-300 border-3 flex items-center justify-center ";
              
              if (disableButtons) {
                if (choice.char === targetLetter.char) {
                  buttonClass += "bg-gradient-to-br from-green-400 to-green-500 border-green-600 text-white shadow-2xl transform scale-110 animate-pulse";
                } else if (isCorrect === false) {
                  buttonClass += "bg-gradient-to-br from-red-400 to-red-500 border-red-600 text-white opacity-60";
                } else {
                  buttonClass += "bg-gray-200 border-gray-300 text-gray-500 opacity-60";
                }
              } else {
                buttonClass += "bg-gradient-to-br from-gray-50 to-gray-100 border-gray-300 hover:from-blue-50 hover:to-blue-100 hover:border-blue-400 cursor-pointer transform hover:scale-110 hover:shadow-2xl text-gray-800";
              }

              return (
                <button
                  key={idx}
                  disabled={disableButtons}
                  onClick={() => handleChoice(choice)}
                  className={buttonClass}
                >
                  {choice.char}
                </button>
              );
            })}
          </div>

          {/* Feedback */}
          {isCorrect !== null && (
            <div className={`text-center p-3 rounded-2xl border-3 ${
              isCorrect 
                ? 'text-green-600 bg-green-100 border-green-300' 
                : 'text-red-600 bg-red-100 border-red-300'
            }`}>
              <div className={`text-3xl md:text-4xl mb-1 ${
                isCorrect ? 'animate-bounce' : 'animate-pulse'
              }`}>
                {isCorrect ? '🎉' : '❌'}
              </div>
              <p className="text-lg md:text-xl font-bold">
                {isCorrect ? 'Excellent! Correct!' : 'Try Again!'}
              </p>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-3 md:p-4 shadow-xl">
          <div className="flex gap-3 justify-center flex-wrap">
            <button 
              onClick={resetGame}
              className="px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
            >
              <RotateCcw size={18} />
              Reset Game
            </button>
            <button 
              onClick={() => setShowInstructions(true)}
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
            >
              <HelpCircle size={18} />
              How to Play
            </button>
          </div>
        </div>

        {/* Celebration Effects */}
        {celebrations && (
          <div className="fixed inset-0 pointer-events-none z-40">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-6xl md:text-9xl animate-bounce">🎉</div>
            </div>
          </div>
        )}

        {sparkles && (
          <div className="fixed inset-0 pointer-events-none z-40">
            {[...Array(15)].map((_, i) => (
              <div
                key={`sparkle-${i}`}
                className="absolute animate-ping text-yellow-400 text-2xl"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`
                }}
              >
                ✨
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Instructions Modal */}
      {showInstructions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-4 border-purple-300">
            <div className="p-4 md:p-8">
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-purple-600">📖 How to Play Phonic Fun!</h2>
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl md:text-3xl font-bold w-10 h-10 flex items-center justify-center"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 md:space-y-6">
                <div className="bg-gradient-to-r from-blue-100 to-blue-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-blue-300">
                  <h3 className="font-bold text-blue-800 mb-2 md:mb-3 text-lg md:text-xl">🎯 Your Mission</h3>
                  <p className="text-blue-700 text-sm md:text-lg">Listen to the Arabic letter sound or name, then click on the correct letter from the choices!</p>
                </div>
                
                <div className="bg-gradient-to-r from-green-100 to-green-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-green-300">
                  <h3 className="font-bold text-green-800 mb-2 md:mb-3 text-lg md:text-xl">🎮 How to Play</h3>
                  <ul className="list-disc list-inside text-green-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>Choose between "Letter Sound" or "Letter Name" mode</li>
                    <li>Click "Play" to hear the Arabic letter</li>
                    <li>Look at the 6 letter choices and click the correct one</li>
                    <li>Build up your streak by getting multiple correct answers!</li>
                    <li>Try to achieve 100% accuracy!</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-yellow-100 to-yellow-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-yellow-300">
                  <h3 className="font-bold text-yellow-800 mb-2 md:mb-3 text-lg md:text-xl">🔊 Sound Modes</h3>
                  <ul className="list-disc list-inside text-yellow-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li><strong>Letter Sound:</strong> Hear the phonetic sound the letter makes</li>
                    <li><strong>Letter Name:</strong> Hear the traditional name of the letter</li>
                    <li>Switch between modes to practice both aspects!</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-purple-100 to-purple-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-purple-300">
                  <h3 className="font-bold text-purple-800 mb-2 md:mb-3 text-lg md:text-xl">📊 Scoring</h3>
                  <ul className="list-disc list-inside text-purple-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li><strong>Rounds:</strong> Total questions answered</li>
                    <li><strong>Correct:</strong> Number of right answers</li>
                    <li><strong>Streak:</strong> Current consecutive correct answers</li>
                    <li><strong>Best:</strong> Your highest streak achieved</li>
                    <li><strong>Accuracy:</strong> Percentage of correct answers</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-red-100 to-red-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-red-300">
                  <h3 className="font-bold text-red-800 mb-2 md:mb-3 text-lg md:text-xl">💡 Pro Tips</h3>
                  <ul className="list-disc list-inside text-red-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>Listen carefully to the audio - you can replay it anytime</li>
                    <li>Start with Letter Sound mode, then try Letter Name</li>
                    <li>Focus on learning the letter shapes and sounds</li>
                    <li>Don't rush - accuracy is more important than speed</li>
                    <li>Use "Reset Game" to start fresh and improve your accuracy</li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-4 md:mt-8 text-center">
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Let's Start Matching! 🎵
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LetterSoundMatchGame;